import { useCallback, useState } from 'react'
import baseUrl from '../../api/baseUrl'
import LabOrderItemsEditor from './LabOrderItemsEditor'
import type { LabOrderDetail } from './labOrderTypes'
import {
  type InvoiceLineForm,
  computeOrderTotals,
  linesFromOrder,
  orderLinesError,
} from './labOrderFormUtils'
import { useLabCatalog, useLabPriceList } from './useLabCatalog'

type LocalOrderItemsEditorProps = {
  order: LabOrderDetail
  onUpdated: (order: LabOrderDetail) => void
}

/**
 * Items and prices of a self-tracked order (a lab the clinic added by hand).
 * The clinic may save the order without prices and fill them in once the lab
 * quotes; the server recomputes totals and payment status from the lines.
 */
export default function LocalOrderItemsEditor({ order, onUpdated }: LocalOrderItemsEditorProps) {
  const [editing, setEditing] = useState(false)
  const [lines, setLines] = useState<InvoiceLineForm[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const partnerId = order.labPartnerId || ''
  const { items: priceList, loading: priceListLoading } = useLabPriceList('', partnerId, editing && !!partnerId)
  const { catalog } = useLabCatalog(editing)
  const setEditorLines = useCallback((next: InvoiceLineForm[]) => setLines(next), [])

  const unpriced = order.invoiceItems.filter((l) => !(Number(l.price) > 0)).length

  function startEditing() {
    setLines(linesFromOrder(order.invoiceItems as unknown as Array<Record<string, unknown>>, { registered: false }))
    setError('')
    setEditing(true)
  }

  async function save() {
    const problem = orderLinesError(lines)
    if (problem) {
      setError(problem)
      return
    }
    setSaving(true)
    setError('')
    try {
      const { invoiceItems } = computeOrderTotals({
        invoiceItems: lines,
        generalDiscount: order.generalDiscount,
        deliveryCharge: order.deliveryCharge,
      })
      const res = await baseUrl.patch(`client/labs/orders/${encodeURIComponent(order._id)}`, { invoiceItems })
      onUpdated(res.data.data)
      setEditing(false)
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to save items',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Items &amp; prices</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {unpriced > 0
              ? `${unpriced} item${unpriced === 1 ? '' : 's'} without a price. Add them when the lab tells you.`
              : 'Change prices here if the lab revises them.'}
          </p>
        </div>
        {!editing ? (
          <button
            type="button"
            onClick={startEditing}
            className="rounded-lg border border-primary px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/5"
          >
            {unpriced > 0 ? 'Add prices' : 'Edit items'}
          </button>
        ) : null}
      </div>

      {editing ? (
        <div className="mt-3 space-y-3">
          <LabOrderItemsEditor
            registered={false}
            labName={order.labName}
            priceList={priceList}
            priceListLoading={priceListLoading}
            catalog={catalog}
            toothNos={order.toothNos || []}
            lines={lines}
            onChange={setEditorLines}
            disabled={saving}
          />
          {error ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 dark:border-slate-600 dark:text-slate-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
